<!--
  TR-40866: newsletter opt-in question after guest registration/login.
  Shared block, rendered inside the login modal (newsletter step) and inside
  the One-Tap-look card (GuestNewsletterToast).
  - accept -> immediate subscribe (logged-in SOI, no confirmation mail),
    then the prompt closes.
  - dismiss -> dismissed server-side, then the prompt closes.
  Either answer is persisted on the guest, the prompt never returns.
-->
<template>
  <div>
    <p class="font-semibold text-neutral-900 mb-1">
      <UiLanguagePlaceholder domain="GuestNewsletterPrompt" name="headline" />
    </p>
    <p class="text-sm text-neutral-800 mb-4">
      <UiLanguagePlaceholder domain="GuestNewsletterPrompt" name="body" />
    </p>

    <p v-if="errorMsg" class="mb-3 text-sm text-cta-700" role="alert">{{ errorMsg }}</p>

    <!-- Accept is full width like the login submit button, dismiss sits
         below as a plain gray text button (no background). -->
    <button
      type="button"
      :disabled="pending !== null"
      :class="[
        'w-full rounded-xl bg-petrol-500 py-3 px-4 font-semibold text-white hover:bg-petrol-600',
        pending === 'subscribe' ? 'opacity-60 cursor-not-allowed' : '',
      ]"
      @click="answer('subscribe')"
    >
      <UiLanguagePlaceholder domain="GuestNewsletterPrompt" name="accept" />
    </button>
    <div class="flex justify-center mt-2">
      <button
        type="button"
        :disabled="pending !== null"
        :class="[
          'py-2 px-4 text-sm text-neutral-800 hover:text-neutral-900 cursor-pointer',
          pending === 'dismiss' ? 'opacity-60' : '',
        ]"
        @click="answer('dismiss')"
      >
        <UiLanguagePlaceholder domain="GuestNewsletterPrompt" name="dismiss" />
      </button>
    </div>

    <p class="text-xs text-neutral-500 mt-3">
      <UiLanguagePlaceholder domain="GuestNewsletterPrompt" name="privacy_note" :variables="{ url: '/datenschutz' }" />
    </p>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  // Where the prompt is shown — only used for the activity log.
  source: 'modal' | 'one_tap' | 'returning'
}>()

const emit = defineEmits<{ (e: 'answered', choice: 'subscribe' | 'dismiss'): void }>()

const store = useGuestStore()
const { log } = useGuestActivityLog()
const { translate: t } = useTranslations('GuestNewsletterPrompt')

const pending = ref<'subscribe' | 'dismiss' | null>(null)
const errorMsg = ref('')

async function answer (decision: 'subscribe' | 'dismiss') {
  if (pending.value !== null) {
    return
  }
  pending.value = decision
  errorMsg.value = ''
  try {
    await store.answerNewsletterPrompt(decision, props.source)
    log(decision === 'subscribe' ? 'newsletter_prompt_accepted' : 'newsletter_prompt_dismissed', { source: props.source })
    finish(decision)
  } catch {
    errorMsg.value = t.value('error')
  } finally {
    pending.value = null
  }
}

function finish (choice: 'subscribe' | 'dismiss') {
  // Flips newsletter_prompt_pending to false — unmounts prompt UIs that are
  // v-if-bound to the flag (e.g. the toast card).
  store.clearNewsletterPromptPending()
  emit('answered', choice)
}
</script>
